import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FiX, FiSun, FiCrop, FiDroplet, FiScissors, FiSlash, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import { useT } from '../../utils/i18n';

const ASPECTS = ['16:9', '9:16', '1:1', '4:5', '4:3'];
const ASPECT_VALUES = { '16:9': 16 / 9, '9:16': 9 / 16, '1:1': 1, '4:5': 4 / 5, '4:3': 4 / 3 };

// A checkerboard behind the picture so transparent areas read as transparent.
export const CHECKERBOARD_STYLE = {
  backgroundColor: '#fff',
  backgroundImage: 'conic-gradient(#e5e7eb 25%, transparent 25% 50%, #e5e7eb 50% 75%, transparent 75%)',
  backgroundSize: '16px 16px',
};

const TOOLS = [
  { id: 'adjust', icon: FiSun, label: 'mediaAI.editor.adjust' },
  { id: 'resize', icon: FiCrop, label: 'mediaAI.editor.resize' },
  { id: 'blur', icon: FiDroplet, label: 'mediaAI.editor.blur' },
  { id: 'remove_background', icon: FiScissors, label: 'mediaAI.editor.removeBg' },
  { id: 'remove_object', icon: FiSlash, label: 'mediaAI.editor.removeObject' },
];

const DEFAULT_ADJUST = { brightness: 0, contrast: 0, saturation: 0, sharpness: 0 };

const Slider = ({ label, value, min, max, onChange }) => (
  <label className="block">
    <span className="flex justify-between text-xs text-gray-600 mb-1">
      <span>{label}</span>
      <span className="tabular-nums text-gray-400">{value > 0 && min < 0 ? `+${value}` : value}</span>
    </span>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-primary-600"
    />
  </label>
);

const Segmented = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-1 p-0.5 rounded-lg bg-gray-100 text-xs font-medium">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`flex-1 px-2.5 py-1.5 rounded-md whitespace-nowrap transition-colors ${
          value === o.value ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/**
 * Editor for one picture in a Media AI chat. It only collects the choice —
 * the tool, its settings and, for brush tools, a mask — and hands it to
 * onApply; the server does the actual edit and adds the result to the chat.
 *
 * The mask is a PNG at the picture's own resolution: painted pixels opaque,
 * everything else transparent.
 */
const ImageEditor = ({ image, imageUrl, onApply, onClose }) => {
  const t = useT();
  const [tool, setTool] = useState('adjust');
  const [adjust, setAdjust] = useState(DEFAULT_ADJUST);
  const [aspect, setAspect] = useState('16:9');
  const [resizeMode, setResizeMode] = useState('crop');
  const [custom, setCustom] = useState({ width: image.width || 1280, height: image.height || 720 });
  const [blurArea, setBlurArea] = useState('selection');
  const [blurAmount, setBlurAmount] = useState(40);
  const [description, setDescription] = useState('');
  const [brushSize, setBrushSize] = useState(6); // % of the picture's shorter edge
  const [strokes, setStrokes] = useState([]);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [natural, setNatural] = useState({ width: image.width || 0, height: image.height || 0 });
  const [cropFocus, setCropFocus] = useState({ x: 0.5, y: 0.5 }); // centre of the crop frame, 0–1

  const canvasRef = useRef(null);
  const drawingRef = useRef(null); // the stroke in progress
  const cropDragRef = useRef(null);

  const brushActive = tool === 'remove_object' || (tool === 'blur' && blurArea === 'selection');
  const usesAI = tool === 'remove_background' || tool === 'remove_object'
    || (tool === 'blur' && blurArea === 'background') || (tool === 'resize' && resizeMode === 'extend' && aspect !== 'custom');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !applying) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [applying, onClose]);

  // ── Brush ─────────────────────────────────────────────────────────────────
  const brushPx = Math.max(4, Math.round((Math.min(natural.width, natural.height) || 1000) * brushSize / 100));

  const redraw = useCallback((list) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#ef4444';
    ctx.fillStyle = '#ef4444';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of list) {
      ctx.lineWidth = stroke.size;
      if (stroke.points.length === 1) {
        const [p] = stroke.points;
        ctx.beginPath();
        ctx.arc(p.x, p.y, stroke.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        stroke.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.stroke();
      }
    }
  }, []);

  useEffect(() => { redraw(strokes); }, [strokes, redraw, natural]);

  const pointFor = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const onPointerDown = (e) => {
    if (!brushActive || applying) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = { size: brushPx, points: [pointFor(e)] };
    redraw([...strokes, drawingRef.current]);
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current.points.push(pointFor(e));
    redraw([...strokes, drawingRef.current]);
  };

  const onPointerUp = () => {
    if (!drawingRef.current) return;
    const stroke = drawingRef.current;
    drawingRef.current = null;
    setStrokes((prev) => [...prev, stroke]);
  };

  const maskBlob = () => new Promise((resolve) => canvasRef.current.toBlob(resolve, 'image/png'));

  // ── Apply ─────────────────────────────────────────────────────────────────
  const apply = async () => {
    setError('');
    let params = {};
    if (tool === 'adjust') params = adjust;
    if (tool === 'resize') {
      params = aspect === 'custom'
        ? { aspect: 'custom', width: custom.width, height: custom.height }
        : { aspect, mode: resizeMode };
      if (resizeMode === 'crop' || aspect === 'custom') params = { ...params, focusX: cropFocus.x, focusY: cropFocus.y };
    }
    if (tool === 'blur') params = { area: blurArea, amount: blurAmount };
    if (tool === 'remove_object') params = { description };

    if (brushActive && !strokes.length) {
      setError(t('mediaAI.editor.paintFirst'));
      return;
    }
    setApplying(true);
    try {
      await onApply({ tool, params, mask: brushActive ? await maskBlob() : null });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t('mediaAI.editor.failed'));
      setApplying(false);
    }
  };

  const adjustChanged = Object.values(adjust).some(Boolean);
  const canApply = !applying && (tool !== 'adjust' || adjustChanged);

  // Live preview where the browser can show it: colour adjustments as CSS
  // filters, and the crop frame for a resize.
  const previewFilter = tool === 'adjust'
    ? `brightness(${1 + adjust.brightness / 100}) contrast(${1 + adjust.contrast / 100}) saturate(${1 + adjust.saturation / 100})`
    : tool === 'blur' && blurArea === 'all' ? `blur(${Math.max(1, blurAmount / 8)}px)` : 'none';

  const imageRatio = natural.width && natural.height ? natural.width / natural.height : 16 / 9;
  const targetRatio = aspect === 'custom' ? (custom.width / custom.height || imageRatio) : ASPECT_VALUES[aspect];
  const showCropFrame = tool === 'resize' && (resizeMode === 'crop' || aspect === 'custom');
  const cropFrame = targetRatio > imageRatio
    ? { width: 100, height: (imageRatio / targetRatio) * 100 }
    : { width: (targetRatio / imageRatio) * 100, height: 100 };
  // Frame position in %, kept inside the picture; matches how the server crops.
  const cropLeft = Math.min(100 - cropFrame.width, Math.max(0, cropFocus.x * 100 - cropFrame.width / 2));
  const cropTop = Math.min(100 - cropFrame.height, Math.max(0, cropFocus.y * 100 - cropFrame.height / 2));

  // Dragging the crop frame moves its centre with the pointer.
  const onCropPointerDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    cropDragRef.current = {
      rect,
      startX: e.clientX,
      startY: e.clientY,
      // Start from where the frame is drawn, not an out-of-range stored focus.
      focus: { x: (cropLeft + cropFrame.width / 2) / 100, y: (cropTop + cropFrame.height / 2) / 100 },
    };
  };
  const onCropPointerMove = (e) => {
    const drag = cropDragRef.current;
    if (!drag) return;
    setCropFocus({
      x: Math.min(1, Math.max(0, drag.focus.x + (e.clientX - drag.startX) / drag.rect.width)),
      y: Math.min(1, Math.max(0, drag.focus.y + (e.clientY - drag.startY) / drag.rect.height)),
    });
  };
  const onCropPointerUp = () => { cropDragRef.current = null; };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/70 sm:p-4">
      <div className="flex flex-col w-full max-w-6xl max-h-full bg-white sm:rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">{t('mediaAI.editor.title')}</h2>
          <button onClick={onClose} disabled={applying} className="text-gray-400 hover:text-gray-700 disabled:opacity-40" aria-label="Close">
            <FiX size={20} />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">
          {/* Picture */}
          <div className="flex-1 min-h-[260px] flex items-center justify-center bg-gray-900 p-3 sm:p-6">
            <div className="relative inline-block max-w-full" style={CHECKERBOARD_STYLE}>
              <img
                src={imageUrl}
                alt=""
                draggable={false}
                onLoad={(e) => setNatural({ width: e.target.naturalWidth, height: e.target.naturalHeight })}
                className="block max-w-full max-h-[45vh] lg:max-h-[70vh] w-auto h-auto select-none"
                style={{ filter: previewFilter }}
              />
              {showCropFrame && (
                <div className="absolute inset-0 overflow-hidden">
                  <div
                    onPointerDown={onCropPointerDown}
                    onPointerMove={onCropPointerMove}
                    onPointerUp={onCropPointerUp}
                    onPointerCancel={onCropPointerUp}
                    className="absolute border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.55)] cursor-move touch-none"
                    style={{ left: `${cropLeft}%`, top: `${cropTop}%`, width: `${cropFrame.width}%`, height: `${cropFrame.height}%` }}
                  />
                </div>
              )}
              {natural.width > 0 && (
                <canvas
                  ref={canvasRef}
                  width={natural.width}
                  height={natural.height}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  className={`absolute inset-0 w-full h-full opacity-50 touch-none ${brushActive ? 'cursor-crosshair' : 'pointer-events-none hidden'}`}
                />
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="w-full lg:w-80 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col lg:overflow-y-auto">
            <div className="grid grid-cols-5 lg:grid-cols-1 gap-1 p-2 border-b border-gray-100">
              {TOOLS.map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setTool(id); setError(''); }}
                  disabled={applying}
                  className={`flex flex-col lg:flex-row items-center gap-1 lg:gap-2.5 px-2 py-2 rounded-lg text-[11px] lg:text-sm text-center lg:text-left transition-colors ${
                    tool === id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={16} className="flex-shrink-0" />
                  <span className="leading-tight">{t(label)}</span>
                </button>
              ))}
            </div>

            <div className="flex-1 p-4 space-y-4">
              {tool === 'adjust' && (
                <>
                  <Slider label={t('mediaAI.editor.brightness')} value={adjust.brightness} min={-100} max={100} onChange={(v) => setAdjust((a) => ({ ...a, brightness: v }))} />
                  <Slider label={t('mediaAI.editor.contrast')} value={adjust.contrast} min={-100} max={100} onChange={(v) => setAdjust((a) => ({ ...a, contrast: v }))} />
                  <Slider label={t('mediaAI.editor.saturation')} value={adjust.saturation} min={-100} max={100} onChange={(v) => setAdjust((a) => ({ ...a, saturation: v }))} />
                  <Slider label={t('mediaAI.editor.sharpness')} value={adjust.sharpness} min={0} max={100} onChange={(v) => setAdjust((a) => ({ ...a, sharpness: v }))} />
                  <button type="button" onClick={() => setAdjust(DEFAULT_ADJUST)} className="text-xs text-gray-500 hover:text-gray-800">
                    {t('mediaAI.editor.reset')}
                  </button>
                </>
              )}

              {tool === 'resize' && (
                <>
                  <Segmented
                    value={aspect}
                    onChange={setAspect}
                    options={[...ASPECTS.map((a) => ({ value: a, label: a })), { value: 'custom', label: t('mediaAI.editor.custom') }]}
                  />
                  {aspect === 'custom' ? (
                    <div className="flex items-end gap-2">
                      {['width', 'height'].map((dim) => (
                        <label key={dim} className="flex-1">
                          <span className="block text-xs text-gray-600 mb-1">{t(`mediaAI.editor.${dim}`)} (px)</span>
                          <input
                            type="number"
                            min={64}
                            max={4096}
                            value={custom[dim]}
                            onChange={(e) => setCustom((c) => ({ ...c, [dim]: Number(e.target.value) || 0 }))}
                            className="w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        </label>
                      ))}
                    </div>
                  ) : (
                    <Segmented
                      value={resizeMode}
                      onChange={setResizeMode}
                      options={[{ value: 'crop', label: t('mediaAI.editor.crop') }, { value: 'extend', label: t('mediaAI.editor.extend') }]}
                    />
                  )}
                  <p className="text-xs text-gray-500">
                    {resizeMode === 'extend' && aspect !== 'custom' ? t('mediaAI.editor.extendHint') : t('mediaAI.editor.cropDragHint')}
                  </p>
                </>
              )}

              {tool === 'blur' && (
                <>
                  <Segmented
                    value={blurArea}
                    onChange={setBlurArea}
                    options={[
                      { value: 'selection', label: t('mediaAI.editor.blurSelection') },
                      { value: 'all', label: t('mediaAI.editor.blurAll') },
                      { value: 'background', label: t('mediaAI.editor.blurBackground') },
                    ]}
                  />
                  <Slider label={t('mediaAI.editor.amount')} value={blurAmount} min={1} max={100} onChange={setBlurAmount} />
                </>
              )}

              {tool === 'remove_background' && (
                <p className="text-sm text-gray-600">{t('mediaAI.editor.removeBgHint')}</p>
              )}

              {tool === 'remove_object' && (
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={300}
                  placeholder={t('mediaAI.editor.objectDescription')}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              )}

              {brushActive && (
                <div className="space-y-3 rounded-xl bg-gray-50 border border-gray-200 p-3">
                  <p className="text-xs text-gray-600">{t('mediaAI.editor.paintHint')}</p>
                  <Slider label={t('mediaAI.editor.brushSize')} value={brushSize} min={1} max={20} onChange={setBrushSize} />
                  <div className="flex gap-3 text-xs">
                    <button
                      type="button"
                      onClick={() => setStrokes((s) => s.slice(0, -1))}
                      disabled={!strokes.length}
                      className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 disabled:opacity-40"
                    >
                      <FiRotateCcw size={12} /> {t('mediaAI.editor.undo')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setStrokes([])}
                      disabled={!strokes.length}
                      className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 disabled:opacity-40"
                    >
                      <FiTrash2 size={12} /> {t('mediaAI.editor.clear')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-100 space-y-2">
              {error && <p className="text-xs text-danger-600">{error}</p>}
              {usesAI && <p className="text-xs text-gray-500">{t('mediaAI.editor.aiNote')}</p>}
              <button
                type="button"
                onClick={apply}
                disabled={!canApply}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white text-sm font-medium py-2.5 transition-colors"
              >
                {applying && <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                {applying ? t('mediaAI.editor.applying') : t('mediaAI.editor.apply')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
